// Viewer presence API. Wraps the WP-AJAX proxies that forward to
// /api/wp/viewers POST (heartbeat) and GET (read summary).
//
// The plugin keeps one sessionId per modal session and POSTs every ~15s while
// the visitor is in the booking flow (step 3). The GET is cheap and called
// per-slot on the calendar plus once per poll inside step-3 (in "none" mode)
// to refresh the displayed count.
//
// KILL SWITCH: the per-slot calendar fan-out plus the 15s step-3 heartbeat
// were thrashing the WP server (every visitor multiplies AJAX → portal proxy
// calls). Set this to false to stop emitting ANY presence traffic without
// ripping out the feature. Both functions then resolve to the same zeros they
// return on transport failure, so every caller degrades to "no one present"
// (badges drop off, the step-3 row hides) — no other code path changes.
// Flip back to true to re-enable once the proxy can take the load.
const VIEWERS_ENABLED = false;

/**
 * Send a presence heartbeat for the (eventId, day) the visitor is on.
 * @returns {Promise<{count: number, totalSeats: number, ttlSeconds?: number}>}
 *          Returns zeros on transport failure — callers should never throw on
 *          a missed heartbeat; the count just lags briefly.
 */
export async function heartbeatViewer({ eventId, day, sessionId, adults = 0, children = 0 }) {
    if (!VIEWERS_ENABLED) return { count: 0, totalSeats: 0, ttlSeconds: 0 };
    const body = new URLSearchParams({
        action     : 'nalu_viewers_heartbeat',
        security   : naluBookingVars.security,
        event_id   : String(eventId),
        day        : day,
        session_id : sessionId,
        adults     : String(adults),
        children   : String(children),
    });
    try {
        const res = await fetch(naluBookingVars.ajax_url, {
            method: 'POST',
            body,
            credentials: 'same-origin',
        });
        const json = await res.json();
        if (!json?.success) return { count: 0, totalSeats: 0 };
        return {
            count: Number(json.data?.count ?? 0) || 0,
            totalSeats: Number(json.data?.totalSeats ?? 0) || 0,
            ttlSeconds: Number(json.data?.ttlSeconds ?? 0) || 0,
        };
    } catch (_) {
        return { count: 0, totalSeats: 0 };
    }
}

/**
 * Read the viewer summary for a single (eventId, day). Used by the calendar
 * carousel to badge per-slot counts and by the step-3 "X people booking now"
 * display in "none" mode.
 * @returns {Promise<{count: number, totalSeats: number}>}
 */
export async function getViewers({ eventId, day }) {
    if (!VIEWERS_ENABLED) return { count: 0, totalSeats: 0 };
    const params = new URLSearchParams({
        action   : 'nalu_get_viewers',
        security : naluBookingVars.security,
        nonce    : naluBookingVars.security,
        event_id : String(eventId),
        day      : day,
    });
    try {
        const res = await fetch(`${naluBookingVars.ajax_url}?${params}`, {
            method: 'GET',
            credentials: 'same-origin',
        });
        const json = await res.json();
        if (!json?.success) return { count: 0, totalSeats: 0 };
        return {
            count: Number(json.data?.count ?? 0) || 0,
            totalSeats: Number(json.data?.totalSeats ?? 0) || 0,
        };
    } catch (_) {
        return { count: 0, totalSeats: 0 };
    }
}

/**
 * Generate a sessionId. Uses crypto.randomUUID when available (modern
 * browsers, served via HTTPS), falls back to a Math.random-based UUIDv4 for
 * older Safari / non-secure contexts. The portal accepts any RFC4122 UUID.
 */
export function generateSessionId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch (_) { /* fall through */ }
    // RFC4122 v4 fallback. Sufficient for opaque session identification.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
